import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { cashdeskKeys } from './cashdeskKeys'

/**
 * Kassa amalining natijasi — UCH TOVUSH, ikki emas.
 *
 * `success` — pul ko'chdi
 * `partial` — pul KO'CHDI, lekin yon ta'sir to'liq bo'lmadi (masalan
 *             abonent qayta ulanmadi). Amalni QAYTA bajarish xato
 *             bo'lardi — pul ikkinchi marta yechilardi
 * `refused` — SERVER ANIQ JAVOB BERDI: amal bajarilmadi va pul ko'chmadi.
 *             Eng ko'p uchraydigan sabab — mablag' yetmasligi. Bu texnik
 *             xato EMAS va qizil ko'rsatilmaydi: kassir summani
 *             o'zgartiradi yoki depozitini to'ldiradi (`CLAUDE.md` §8)
 * `error`   — haqiqiy nosozlik: tarmoq uzildi, 403, 500
 */
export type CashTone = 'success' | 'partial' | 'refused' | 'error'

export interface CashFeedback {
  tone: CashTone
  message: string
  /** Qo'shimcha aniqlik: "Depozitingiz: 12 000" kabi */
  hint?: string
}

export interface CashOperation {
  feedback: CashFeedback | null
  running: boolean
  run: (action: () => Promise<CashFeedback>) => Promise<CashFeedback>
  reset: () => void
}

/**
 * Pul amalini bajaradi va natijani UCH holatga ajratadi.
 *
 * <h2>⚠️ NEGA `shared/ui/useOperationRunner` ISHLATILMADI</h2>
 * U ATAYLAB teskarisini qiladi: tashlangan xatoni ham `FAILED` ga
 * aylantiradi va ikkala holat bitta qizil banner bo'lib chiqadi. Admin
 * ekranlari uchun bu to'g'ri — operator uchun ikkalasi ham "bajarilmadi".
 *
 * Kassa uchun esa bu AYNAN yo'l qo'yilmaydigan narsa. Kassir abonent
 * qarshisida turibdi va ikki holatda IKKI XIL ish qiladi:
 *   · «mablag' yetmadi» — o'z depozitini to'ldiradi yoki summani kamaytiradi
 *   · «xato»            — qayta uriniladi, chunki amal umuman bormagan
 * Ikkalasi bir xil ko'rinsa, kassir yetmagan mablag'ni "tarmoq nosozligi"
 * deb o'ylab tugmani qayta-qayta bosadi.
 *
 * Kesh bitta PREFIKS bo'yicha bekor qilinadi (`cashdesk`): pul harakati
 * abonentning balansini ham, kassirning depozitini ham o'zgartiradi va
 * ikkalasi ham shu prefiks ostida. Kalitlarni birma-bir sanash — yangi
 * kalit qo'shilganda uni bu yerga qo'shishni unutish, ya'ni ekranda
 * jimgina eskirgan PUL SUMMASI qolishi demak.
 */
export function useCashOperation(): CashOperation {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [feedback, setFeedback] = useState<CashFeedback | null>(null)
  const [running, setRunning] = useState(false)

  const run = useCallback(
    async (action: () => Promise<CashFeedback>): Promise<CashFeedback> => {
      setRunning(true)
      setFeedback(null)
      try {
        const outcome = await action()
        setFeedback(outcome)
        // ⚠️ `refused` da ham bekor qilinadi: server rad etganini ekrandagi
        // eski depozit summasi bilan tekshirib bo'lmaydi va kassir aynan
        // o'sha songa qarab keyingi qarorni qabul qiladi.
        void queryClient.invalidateQueries({ queryKey: cashdeskKeys.all })
        return outcome
      } catch (error) {
        const failure: CashFeedback = { tone: 'error', message: translateError(error, t) }
        setFeedback(failure)
        return failure
      } finally {
        setRunning(false)
      }
    },
    [queryClient, t],
  )

  const reset = useCallback(() => setFeedback(null), [])

  return { feedback, running, run, reset }
}
